import ActionModal from '@/components/ActionModal';
import NavbarGlobal from '@/components/NavbarGlobal';
import { RoutesList } from '@/routes/routesList';
import useWeeklyPlan from '@/services/plans/hooks/useWeeklyPlan';
import { useActionModal } from '@/stores/useActionModal';
import { useCalendarMonthlyStore } from '@/stores/useCalendar';
import { usePeopleStore } from '@/stores/usePeopleStore';
import { showSecondaryNavItemsBasedOnLocation } from '@/utils/index';
import rem from '@/utils/rem';
import { Container, ContainerProps, Flex, Spinner, VStack } from '@chakra-ui/react';
import { ReactNode, Suspense, lazy, memo, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation, useParams } from 'react-router-dom';

const MySecondaryNavbar = lazy(() => import('@/components/SecondaryNavbar'));

const withPlaceholderPages = [
  RoutesList.ProjectsPage,
  RoutesList.PeoplePage,
  RoutesList.ProjectDetailPage,
  RoutesList.PersonPage,
  RoutesList.CategoryManagerDetailsPage,
];

type Props = {
  children: ReactNode;
  pageTitle: string;
  editActionModalTooltip?: string;
  rightSectionElements?: JSX.Element;
  leftSectionElements?: JSX.Element;
  forceShowSecondaryNav?: boolean;
  paddingTop?: ContainerProps['paddingTop'];
} & ContainerProps;

const DashboardLayout = ({
  children,
  pageTitle,
  editActionModalTooltip = undefined,
  rightSectionElements,
  leftSectionElements,
  forceShowSecondaryNav = false,
  paddingTop = rem(16),
  ...rest
}: Props) => {
  const location = useLocation();
  const params = useParams();

  const showSecondaryNavBar = showSecondaryNavItemsBasedOnLocation(location)?.showSecondaryNavBar;

  const updateSearchQuery = usePeopleStore((state) => state.updateSearchQuery);
  const updateFilterSelected = usePeopleStore((state) => state.updateFilterSelected);

  const { opened: isActionModalOpen, setActionModalOpen, data, createLeverageModalOpen } = useActionModal();

  const selectedDate = useCalendarMonthlyStore((state) => state.selectedDate);

  const withPlaceholder = params?.id
    ? withPlaceholderPages.findIndex((page) => page.replace(/:id/, params.id ?? '') === location.pathname) !== -1
    : withPlaceholderPages.includes(location.pathname);

  useWeeklyPlan({ selectedDate });

  useEffect(() => {
    if (!location.pathname.includes('/people')) {
      updateSearchQuery('');
      updateFilterSelected('all');
    }
  }, [location.pathname, updateFilterSelected, updateSearchQuery]);

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
      </Helmet>

      <Flex
        as="header"
        sx={{
          '@media print': {
            display: 'none',
          },
        }}
        position="sticky"
        zIndex="1201" // --chakra-zIndices-banner + 1
        top={0}
        width="full"
        boxShadow="2px 2px 9px 0px rgba(0, 0, 0, 0.08)"
        transition="right 600ms cubic-bezier(0.65, 0.26, 0, 1.1)"
      >
        <VStack alignItems="flex-start" gap={0} width="full">
          <NavbarGlobal />

          {showSecondaryNavBar && !forceShowSecondaryNav && (
            <Suspense
              fallback={
                <Flex justifyContent="center" width="full">
                  <Spinner size="sm" />
                </Flex>
              }
            >
              <MySecondaryNavbar
                rightSectionElements={rightSectionElements}
                leftSectionElements={leftSectionElements}
              />
            </Suspense>
          )}
        </VStack>
      </Flex>

      <Container
        as="main"
        overflowY="scroll"
        maxWidth="full"
        paddingTop={paddingTop}
        background="background-primary"
        transition="margin-left 600ms cubic-bezier(0.65, 0.26, 0, 1.1)"
        paddingX={rem(16)}
        {...rest}
        sx={{
          '@media print': {
            padding: rem(16),
            overflowY: 'visible',
          },
        }}
      >
        {children}
      </Container>

      {(isActionModalOpen || createLeverageModalOpen) && (
        <ActionModal
          isOpen={isActionModalOpen}
          onClose={() => setActionModalOpen(false, undefined)}
          withPlaceholder={withPlaceholder}
          readonlyTooltipLabel={editActionModalTooltip}
          {...data}
        />
      )}
    </>
  );
};

// https://www.gatsbyjs.com/docs/reference/local-development/fast-refresh/#limitations
const MemoizedDashboardLayout = memo(DashboardLayout);

export default MemoizedDashboardLayout;
